import { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export const ThemeProvider = ({ children }) => {
  // Check localStorage or system preference for initial theme
  const getInitialTheme = () => {
    const savedTheme = localStorage.getItem('dreamlife_theme');
    if (savedTheme) {
      return savedTheme;
    }
    // Default to dark theme for luxury feel
    return 'dark';
  };

  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    // Apply theme to document
    const root = document.documentElement;
    
    if (theme === 'light') {
      root.classList.remove('dark');
      root.classList.add('light');
      // Update CSS variables for light mode
      root.style.setProperty('--color-obsidian', '#ffffff');
      root.style.setProperty('--color-surface', '#f8f9fa');
      root.style.setProperty('--color-card-bg', '#ffffff');
      root.style.setProperty('--color-text-primary', '#1a1a1a');
      root.style.setProperty('--color-text-secondary', '#4b5563');
      root.style.setProperty('--color-text-muted', '#6b7280');
      // Update body background
      document.body.style.backgroundColor = '#ffffff';
      document.body.style.color = '#1a1a1a';
    } else {
      root.classList.remove('light');
      root.classList.add('dark');
      // Restore dark mode CSS variables
      root.style.setProperty('--color-obsidian', '#0a0a0a');
      root.style.setProperty('--color-surface', '#141414');
      root.style.setProperty('--color-card-bg', '#171717');
      root.style.setProperty('--color-text-primary', '#f5f5f5');
      root.style.setProperty('--color-text-secondary', '#a3a3a3');
      root.style.setProperty('--color-text-muted', '#737373');
      // Update body background
      document.body.style.backgroundColor = '#0a0a0a';
      document.body.style.color = '#f5f5f5';
    }
    
    // Save to localStorage
    localStorage.setItem('dreamlife_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const setLightTheme = () => setTheme('light');
  const setDarkTheme = () => setTheme('dark');

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setLightTheme, setDarkTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
